import { useRef } from 'react';
import { GetStaticPathsResult, GetStaticPropsContext, GetStaticPropsResult } from 'next';

import { Box, Heading, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { Markdown } from '~/components/markdown';
import { ShapesBanner } from '~/components/shapes-banner';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import { EnterpriseResourceContainer } from '~/features/enterprise-resource/components/enterprise-resource-container';
import { EnterpriseResourceForm } from '~/features/enterprise-resource/components/enterprise-resource-form';
import { EnterpriseResourceGlyphCard } from '~/features/enterprise-resource/components/enterprise-resource-glyph-card';
import { EnterpriseResourceHeroWithLeadForm } from '~/features/enterprise-resource/components/enterprise-resource-hero-with-lead-form';
import { EnterpriseResourceScrollContainer } from '~/features/enterprise-resource/components/enterprise-resource-scroll-container';
import { getEnterpriseEbookContent } from '~/features/enterprise-resource/controllers/get-enterprise-ebook-content';
import { EnterpriseEbookContent } from '~/features/enterprise-resource/models/enterprise-ebook-content';
import { logger } from '~/features/logging/logger';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';

type EnterpriseEbookPageProps = {
  page: Page;
  enterpriseEbookContent: EnterpriseEbookContent;
};

export default function EnterpriseEbookPage({ page, enterpriseEbookContent }: EnterpriseEbookPageProps) {
  const formRef = useRef<HTMLDivElement>(null);
  const ebookData = enterpriseEbookContent.ebookData;
  const formData = ebookData.form;
  const fileUrl = ebookData.pdfFile.asset.url;

  const cardListItems = ebookData.cardList?.map((cardItem, index) => (
    <Box key={cardItem.title} display='flex' height='100%' justifyContent='center' width='100%'>
      <EnterpriseResourceGlyphCard
        glyphName={cardItem.glyphName}
        height='100%'
        markdownContent={cardItem.markdownContent}
        maxWidth={{ base: '100%', md: '400px' }}
        minWidth={{ base: '100%', md: '360px' }}
        title={cardItem.title}
        topSlot={
          <Text color='blue.500' fontSize='48px'>
            0{index + 1}
          </Text>
        }
      />
    </Box>
  ));

  function handlePrimaryCtaClick() {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    if (ebookData.shapesBanner?.primaryCta?.buttonAction === 'Coded Action') {
      window.open(fileUrl, '_blank');
    }

    trackEvent({
      type: 'click',
      name: 'Enterprise Ebook CTA Clicked',
      label: 'shapes_banner_cta_clicked',
      value: enterpriseEbookContent.slug,
    });
  }

  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.EnterpriseEbook} />
      <PageLayout overflowX='hidden' withoutVerticalPadding>
        <EnterpriseResourceHeroWithLeadForm
          description={ebookData.description}
          relatedCompanies={ebookData.relatedCompanies}
          resourceTypeTitle={ebookData.resourceTypeTitle}
          title={ebookData.title}
          contentSlot={
            <Box paddingRight={{ base: 'unset', lg: '128px' }} paddingTop={{ base: '40px', md: '64px', lg: '40px' }}>
              <Heading as='h2' marginBottom='8px' size='marketing-3xl'>
                {ebookData.pageContent.title}
              </Heading>

              <Markdown markdownText={ebookData.pageContent.body} />
            </Box>
          }
          formSlot={
            <EnterpriseResourceForm
              ref={formRef}
              additionalEventProperties={{ ebookName: enterpriseEbookContent.slug }}
              fileUrl={fileUrl}
              leadCaptureType={ebookData.leadCaptureType}
              resourceType='ebook'
              templateForm={formData}
            />
          }
        />

        {ebookData.cardListTitle && cardListItems && (
          <>
            <EnterpriseResourceContainer
              display='flex'
              justifyContent='center'
              marginTop={{ base: '80px', lg: '120px' }}
            >
              <Heading as='h3' maxWidth='736px' size='marketing-4xl' textAlign='center'>
                {ebookData.cardListTitle}
              </Heading>
            </EnterpriseResourceContainer>

            <Box marginTop={{ base: '24px', lg: '40px' }}>
              <EnterpriseResourceScrollContainer items={cardListItems} slidesPerView='auto' />
            </Box>
          </>
        )}

        <Box h={{ base: '80px', md: '64px', lg: '120px' }} />

        {ebookData.shapesBanner && (
          <ShapesBanner
            {...ebookData.shapesBanner}
            flipTitleAndSubtitleOrder={true}
            onPrimaryCtaClick={handlePrimaryCtaClick}
          />
        )}

        {ebookData.aboutContentTitle && ebookData.aboutContentDescription && (
          <ContentContainer marginY={{ base: '40px', md: '64px', lg: '80px' }}>
            <Box display='flex' flexWrap={{ base: 'wrap', md: 'nowrap' }} marginX={{ base: '0px', lg: '96px' }}>
              <Heading
                as='h3'
                marginBottom={{ base: '16px', md: '0px' }}
                marginRight={{ base: '0px', md: '40px' }}
                size='marketing-2xl'
                whiteSpace='nowrap'
              >
                {ebookData.aboutContentTitle}
              </Heading>

              <Markdown markdownText={ebookData.aboutContentDescription} />
            </Box>
          </ContentContainer>
        )}
      </PageLayout>
    </>
  );
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    fallback: 'blocking',
    paths: [],
  };
}

export async function getStaticProps({
  params,
}: GetStaticPropsContext<{ slug: string }>): Promise<GetStaticPropsResult<EnterpriseEbookPageProps>> {
  const slug = params?.slug;

  if (!slug) {
    logger.fatal('Enterprise ebook slug not found', {
      file: 'src/pages/ebook/[slug].tsx',
      function: 'getStaticProps',
      slug,
    });

    throw new Error('Enterprise ebook slug not found');
  }

  const [page, enterpriseEbookContent] = await Promise.all([getPage('/ebook'), getEnterpriseEbookContent(slug)]);

  if (!enterpriseEbookContent) {
    return {
      notFound: true,
    };
  }

  // If enterpriseEbookContent has seo, override default page seo
  if (enterpriseEbookContent.seo) {
    page.seo = enterpriseEbookContent.seo;
  }

  return {
    props: {
      page,
      enterpriseEbookContent,
    },
  };
}
